import { Feature } from '@frontend/functionality/types';
import { Logger } from '@frontend/logger';
import { useAccountRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserType } from '@frontend/transaction/types';
import type { TransactionUser } from '@frontend/transaction/types';
import type { MobileWizardStateRecord } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScanners } from '../../../hooks';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY, findUsersByEmail, getUserEmailLookupField } from '../../utils/open-drop-off-user-lookup';
import type { OpenDropOffQrUserResolution } from '../../utils/open-drop-off-user-lookup';
import { useScreen } from '../useScreen';
import { parseOpenDropOffQrCodeTransactionInfo, validateOpenDropOffQrCodePattern } from './open-drop-off-scan-qr-code.utils';

interface ViewProps {
    resolving: boolean;
    error: string | null;
    configurationError: string | null;
    resetError: () => void;
    onBack: () => void;
    onNoQrCode: () => Promise<void>;
}

const SCREEN_ID = 'OPEN_DROP_OFF_SCAN_QR_CODE';
const MAX_QR_CODE_LENGTH = 4_096;

const createTransactionUserDraft = (userId: string, type: TransactionUserType, accountId: string, transactionId?: string): Partial<TransactionUser> => {
    const timestamp = new Date().toISOString();

    return {
        user_id: userId,
        type,
        transaction_id: transactionId || '',
        account_id: accountId,
        creation_timestamp: timestamp,
        update_timestamp: timestamp
    };
};

const findNextStep = (store: MobileWizardStateRecord, trigger: ScreenTrigger) =>
    store.steps.find((step) => step.screen === ScreenType.OPEN_DROP_OFF_SCAN_QR_CODE && step.trigger === trigger);

const useOpenDropOffScanQrCode = (): ViewProps => {
    const intl = useIntl();
    const { navigationState, store, wizardId, updateWizardData, onBack, onSubmit } = useScreen();
    const { store: accountStore } = useAccountRepository();
    const { get: getSetting } = useWizardSettings(store);
    const { localUpdateTransaction } = useWizardTransaction({
        wizardId,
        store,
        autoCreate: false,
        autoFetch: false
    });
    const [resolving, setResolving] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const transactionInfoInFlightRef = useRef(false);
    const qrCodePattern = getSetting(SettingKey.ON_SCAN_REGEX_DATA_VALIDATION);
    const patternValidation = useMemo(() => validateOpenDropOffQrCodePattern(qrCodePattern), [qrCodePattern]);
    const configurationError = useMemo(() => {
        if (patternValidation.valid) return null;

        return intl.formatMessage({
            id: 'kiosk.wizard.open-drop-off-scan-qr-code.invalid-configuration',
            description: 'Error shown when the open drop-off QR code pattern setting is missing or invalid',
            defaultMessage: 'This screen is not configured correctly. Please contact support.'
        });
    }, [intl, patternValidation]);

    useScanners({
        id: SCREEN_ID,
        onQrCodeScanned: (qrCode) => {
            if (!patternValidation.valid || transactionInfoInFlightRef.current) return;

            if (qrCode.length > MAX_QR_CODE_LENGTH) {
                setScanError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.open-drop-off-scan-qr-code.invalid-code',
                        description: 'Error shown when a scanned QR code does not match the configured open drop-off QR code pattern',
                        defaultMessage: 'This QR code is not valid. Please try again.'
                    })
                );
                return;
            }

            const handleTransactionInfo = async () => {
                if (!store) return;

                transactionInfoInFlightRef.current = true;
                setResolving(true);
                setScanError(null);

                try {
                    const transactionInfo = parseOpenDropOffQrCodeTransactionInfo(qrCode, patternValidation);
                    if (!transactionInfo) {
                        setScanError(
                            intl.formatMessage({
                                id: 'kiosk.wizard.open-drop-off-scan-qr-code.invalid-code',
                                description: 'Error shown when a scanned QR code does not match the configured open drop-off QR code pattern',
                                defaultMessage: 'This QR code is not valid. Please try again.'
                            })
                        );
                        return;
                    }

                    const accountId = store.wizard.account_id;
                    const lookupField = getUserEmailLookupField(accountStore.availableAccountsInfo, accountId);
                    const [senderCandidates, receiverCandidates] = await Promise.all([
                        findUsersByEmail(accountId, transactionInfo.senderEmail, lookupField),
                        findUsersByEmail(accountId, transactionInfo.receiverEmail, lookupField)
                    ]);
                    const sender = senderCandidates.length === 1 ? senderCandidates[0] : null;
                    const receiver = receiverCandidates.length === 1 ? receiverCandidates[0] : null;
                    const userResolution: OpenDropOffQrUserResolution = {
                        trackingNumber: transactionInfo.trackingNumber,
                        senderEmail: transactionInfo.senderEmail,
                        receiverEmail: transactionInfo.receiverEmail,
                        lookupField,
                        senderCandidates,
                        receiverCandidates
                    };
                    const resolvedUsersData: { senders?: Partial<TransactionUser>[]; receivers?: Partial<TransactionUser>[] } = {};

                    if (sender) {
                        resolvedUsersData.senders = [createTransactionUserDraft(sender.id, TransactionUserType.SENDER, accountId, store.data.transaction?.id)];
                    }

                    if (receiver) {
                        resolvedUsersData.receivers = [
                            createTransactionUserDraft(receiver.id, TransactionUserType.RECEIVER, accountId, store.data.transaction?.id)
                        ];
                    }

                    if (senderCandidates.length === 0 || receiverCandidates.length === 0) {
                        if (sender && receiverCandidates.length === 0) {
                            await localUpdateTransaction({
                                tracking_number: transactionInfo.trackingNumber,
                                sender_ids: [sender.id],
                                iot_id: navigationState.iot?.id ?? null,
                                user_id: navigationState.user?.id ?? null,
                                feature: Feature.OPEN_DROP_OFF
                            });

                            updateWizardData({
                                trackingNumber: transactionInfo.trackingNumber,
                                createReceiverEmail: transactionInfo.receiverEmail,
                                ...resolvedUsersData,
                                [OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY]: userResolution
                            });

                            const nextStep = findNextStep(store, ScreenTrigger.SUBMIT_FALSE);
                            if (!nextStep) {
                                Logger.warn('Open drop-off QR submit_false step is missing for receiver creation');
                                return;
                            }

                            onSubmit(nextStep);
                            return;
                        }

                        Logger.warn('Could not resolve open drop-off QR sender or receiver', {
                            senderEmail: transactionInfo.senderEmail,
                            receiverEmail: transactionInfo.receiverEmail,
                            lookupField,
                            senderCandidateCount: senderCandidates.length,
                            receiverCandidateCount: receiverCandidates.length
                        });
                        setScanError(
                            intl.formatMessage({
                                id: 'kiosk.wizard.open-drop-off-scan-qr-code.user-not-found',
                                description: 'Error shown when open drop-off QR code emails do not match any user in the account',
                                defaultMessage: 'No matching user was found for this QR code. Please try again or continue without a QR code.'
                            })
                        );
                        return;
                    }

                    if (senderCandidates.length > 1 || receiverCandidates.length > 1) {
                        await localUpdateTransaction({
                            tracking_number: transactionInfo.trackingNumber,
                            iot_id: navigationState.iot?.id ?? null,
                            user_id: navigationState.user?.id ?? null,
                            feature: Feature.OPEN_DROP_OFF
                        });

                        updateWizardData({
                            trackingNumber: transactionInfo.trackingNumber,
                            [OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY]: userResolution,
                            ...resolvedUsersData
                        });

                        Logger.log('Open drop-off QR user resolution requires selection', {
                            trackingNumber: transactionInfo.trackingNumber,
                            senderEmail: transactionInfo.senderEmail,
                            receiverEmail: transactionInfo.receiverEmail,
                            lookupField,
                            senderCandidateCount: senderCandidates.length,
                            receiverCandidateCount: receiverCandidates.length
                        });

                        const nextStep = findNextStep(store, ScreenTrigger.SUBMIT_TRUE);
                        if (!nextStep) {
                            Logger.warn('Open drop-off QR submit_true step is missing for ambiguous user resolution');
                            return;
                        }

                        onSubmit(nextStep);
                        return;
                    }

                    if (!sender || !receiver) {
                        Logger.warn('Open drop-off QR user resolution ended in an unexpected state', {
                            senderEmail: transactionInfo.senderEmail,
                            receiverEmail: transactionInfo.receiverEmail,
                            lookupField,
                            senderCandidateCount: senderCandidates.length,
                            receiverCandidateCount: receiverCandidates.length
                        });
                        return;
                    }

                    await localUpdateTransaction({
                        tracking_number: transactionInfo.trackingNumber,
                        sender_ids: [sender.id],
                        receiver_ids: [receiver.id],
                        iot_id: navigationState.iot?.id ?? null,
                        user_id: navigationState.user?.id ?? null,
                        feature: Feature.OPEN_DROP_OFF
                    });

                    updateWizardData({
                        trackingNumber: transactionInfo.trackingNumber,
                        ...resolvedUsersData
                    });

                    Logger.log('Open drop-off QR code processed', {
                        trackingNumber: transactionInfo.trackingNumber,
                        senderId: sender.id,
                        receiverId: receiver.id
                    });

                    const nextStep = findNextStep(store, ScreenTrigger.SUBMIT);
                    onSubmit(nextStep);
                } catch (error) {
                    Logger.error('Failed to handle open drop-off QR code', error);
                    setScanError(
                        intl.formatMessage({
                            id: 'kiosk.wizard.open-drop-off-scan-qr-code.processing-failed',
                            description: 'Error shown when open drop-off QR code processing fails unexpectedly',
                            defaultMessage: 'Something went wrong while reading this QR code. Please try again.'
                        })
                    );
                } finally {
                    transactionInfoInFlightRef.current = false;
                    setResolving(false);
                }
            };

            void handleTransactionInfo();
        }
    });

    const onNoQrCode = async () => {
        if (!store) return;

        const nextStep = store.steps.find((step) => step.screen === ScreenType.OPEN_DROP_OFF_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT_FALSE);
        if (!nextStep) return;

        const senderId = navigationState.user?.id;
        if (senderId) {
            await localUpdateTransaction({
                sender_ids: [senderId],
                user_id: senderId,
                iot_id: navigationState.iot?.id ?? null,
                feature: Feature.OPEN_DROP_OFF
            });

            updateWizardData({
                senders: [createTransactionUserDraft(senderId, TransactionUserType.SENDER, store.wizard.account_id, store.data.transaction?.id)]
            });
        } else {
            Logger.warn('Cannot set open drop-off no-QR sender: no logged-in user found');
        }

        setResolving(false);
        onSubmit(nextStep);
    };

    return {
        resolving,
        error: scanError,
        configurationError,
        resetError: () => setScanError(null),
        onBack,
        onNoQrCode
    };
};

export default useOpenDropOffScanQrCode;
